// src/components/Member/GroceriesPage.js
//
// Full-page version of the grocery list. Mounted at /mymeals/groceries.
// Shares ProductListContent with MealPlan's drawer (drawer variant lives in
// ProductList.js). Layout mirrors MyMealPlans: yellow header area on top with
// just the page title, then flowing content in a rounded/elevated panel. The
// back button is owned by the global Header (navigates to /mymeals); the
// internal sticky category nav inside ProductListContent is the only bottom
// bar on this page.

import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Share2 } from "lucide-react";
import ProductListContent from "../ProductList/ProductListContent";
import { getUserCountry } from "../../services/apiService";
import { useMealPlan } from "../../contexts/MealPlanContext";
import { computeAdjustedTotalFromProducts, computeTotalFromProducts } from "../MealPlan/utils/mealPlanHelpers";
import { getCountryCurrency, normalizeCountryName } from "../../services/countryService";

const GroceriesPage = ({
  productList = [],
  maxStorageTime,
  leftovers,
  initialShoppingModeState,
  initialHaveItState,
  checkedItemsOverride = null,
  onCheckedItemsChange,
  onCreateShareLink,
  shareUrl,
  allowShare = true,
  myProductsOverride = null,
}) => {
  const { t } = useTranslation("mealplan");

  const [userCountry, setUserCountry] = useState(null);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");

  const { setShoppingModeState, setHaveItState, haveItState } = useMealPlan();

  // Price computation for header tags
  const supermarketByCountry = { Spain: "Mercadona", Estonia: "Selver", USA: "Walmart" };
  const normalizedCountry = normalizeCountryName(userCountry || "Spain");
  const currencyInfo = useMemo(() => getCountryCurrency(normalizedCountry), [normalizedCountry]);
  const supermarketName = supermarketByCountry[normalizedCountry] || normalizedCountry;
  const totalPriceNum = useMemo(() => computeTotalFromProducts(productList) || 0, [productList]);
  const adjustedPriceNum = useMemo(() => computeAdjustedTotalFromProducts(productList, haveItState), [productList, haveItState]);
  const displayPriceNum = adjustedPriceNum !== null ? adjustedPriceNum : totalPriceNum;
  const formattedPrice = `${currencyInfo.symbol}${displayPriceNum.toFixed(2)}`;

  // Initialize context with loaded data on mount
  const initedShopping = useRef(false);
  useEffect(() => {
    if (
      !initedShopping.current &&
      initialShoppingModeState &&
      Object.keys(initialShoppingModeState).length > 0
    ) {
      setShoppingModeState(initialShoppingModeState);
      initedShopping.current = true;
    }
  }, [initialShoppingModeState, setShoppingModeState]);

  const initedHaveIt = useRef(false);
  useEffect(() => {
    if (
      !initedHaveIt.current &&
      initialHaveItState &&
      Object.keys(initialHaveItState).length > 0
    ) {
      setHaveItState(initialHaveItState);
      initedHaveIt.current = true;
    }
  }, [initialHaveItState, setHaveItState]);

  // Fetch user country (for currency).
  useEffect(() => {
    (async () => {
      try {
        const c = await getUserCountry();
        if (c) setUserCountry(c);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const handleShare = async () => {
    let resolvedShareUrl =
      shareUrl || (typeof window !== "undefined" ? window.location.href : "");

    const shareData = {
      title: t("grocery.title"),
      text: t("grocery.title"),
      url: resolvedShareUrl,
    };

    const copyLink = async (url) => {
      if (!url || typeof window === "undefined") {
        return false;
      }

      try {
        if (typeof navigator !== "undefined" && navigator.clipboard) {
          await navigator.clipboard.writeText(url);
          return true;
        }
      } catch {
        // Continue to legacy fallback.
      }

      try {
        const input = document.createElement("input");
        input.value = url;
        input.setAttribute("readonly", "");
        input.style.position = "absolute";
        input.style.left = "-9999px";
        document.body.appendChild(input);
        input.select();
        input.setSelectionRange(0, input.value.length);
        const copied = document.execCommand("copy");
        document.body.removeChild(input);
        if (copied) {
          return true;
        }
      } catch {
        // Continue to prompt fallback.
      }

      try {
        window.prompt(
          t("grocery.copyShareLinkPrompt", {
            defaultValue: "Copy this link:",
          }),
          url
        );
      } catch {
        // Ignore as final fallback.
      }

      return false;
    };

    try {
      if (onCreateShareLink) {
        setShareLoading(true);
        try {
          const result = await onCreateShareLink();
          if (result?.url) {
            resolvedShareUrl = result.url;
            shareData.url = result.url;
          }
        } catch {
          // Fall back to existing URL if share-link creation fails.
        }
      }

      const userAgent =
        typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
      const isMobileLike = /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent);
      const canUseNativeShare =
        typeof navigator !== "undefined" &&
        typeof navigator.share === "function" &&
        isMobileLike &&
        (typeof navigator.canShare !== "function" ||
          navigator.canShare({ url: shareData.url }));

      if (canUseNativeShare) {
        try {
          await navigator.share(shareData);
          setShareFeedback(
            t("grocery.shareDialogOpened", {
              defaultValue: "Share dialog opened",
            })
          );
        } catch {
          // If native share fails, fall back to clipboard.
          const copied = await copyLink(shareData.url);
          setShareFeedback(
            copied
              ? t("grocery.linkCopied", { defaultValue: "Link copied" })
              : t("grocery.shareFallbackShown", {
                  defaultValue: "Use the shown link to share",
                })
          );
        }
      } else {
        const copied = await copyLink(shareData.url);
        setShareFeedback(
          copied
            ? t("grocery.linkCopied", { defaultValue: "Link copied" })
            : t("grocery.shareFallbackShown", {
                defaultValue: "Use the shown link to share",
              })
        );
      }
    } catch {
      // Last-resort fallback so the user can still copy the link.
      const copied = await copyLink(shareData.url);
      setShareFeedback(
        copied
          ? t("grocery.linkCopied", { defaultValue: "Link copied" })
          : t("grocery.shareFailed", {
              defaultValue: "Unable to share right now",
            })
      );
    } finally {
      setShareLoading(false);
    }
  };

  useEffect(() => {
    if (!shareFeedback) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setShareFeedback("");
    }, 2200);

    return () => clearTimeout(timeout);
  }, [shareFeedback]);

  return (
    <div className="min-h-safe-screen">
      {/* Yellow header area — title + subtitle + tag row */}
      <div className="px-6 pt-7 pb-4 max-w-5xl mx-auto flex flex-col">
        <h1 className="text-2xl font-bold">{t("grocery.title")}</h1>
        <p className="font-medium text-[#3F3C31] text-[15.2px] leading-[28px] mt-1">
          {t("grocery.pricesFrom", { supermarket: supermarketName })}
        </p>
        <div className="flex gap-2 mt-4 mb-1">
          {/* Price tag */}
          <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-[10px] border-[0.5px] border-[#F3F3F2] bg-[rgba(255,255,255,0.64)] shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)] backdrop-blur-lg">
            {t("grocery.total")} {formattedPrice}
          </span>
          {/* Share tag */}
          {allowShare && (
            <button
              onClick={handleShare}
              disabled={shareLoading}
              className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-[10px] border-[0.5px] border-[#F3F3F2] bg-[rgba(255,255,255,0.64)] shadow-[4px_12px_20px_0px_rgba(83,81,69,0.08)] backdrop-blur-lg cursor-pointer md:hover:bg-[rgba(255,255,255,0.8)] disabled:opacity-60 transition-colors"
              aria-label={t("grocery.share")}
            >
              <Share2 className="w-4 h-4" strokeWidth={1.75} />
              <span aria-live="polite">{shareFeedback || t("grocery.share")}</span>
            </button>
          )}
        </div>
      </div>

      {/* Content panel — rounded top, elevated shadow (matches MyMealPlans) */}
      <div
        className="min-h-0 mt-2 pt-0 pb-32
                    bg-[#F8F8F7]
                    rounded-[12px_12px_0px_0px]
                    border-t-[2px]
                    border-t-[#FFF]
                    shadow-[0px_-4px_24px_0px_rgba(13,12,7,0.12)]"
      >
        <div className="max-w-5xl mx-auto">
          <ProductListContent
            variant="page"
            products={productList}
            maxStorageTime={maxStorageTime}
            leftovers={leftovers}
            shoppingMode={true}
            country={userCountry || "Spain"}
            checkedItemsOverride={checkedItemsOverride}
            onCheckedItemsChange={onCheckedItemsChange}
            myProductsOverride={myProductsOverride}
          />
        </div>
      </div>
    </div>
  );
};

export default GroceriesPage;
